import React, { useState, useEffect } from 'react';
import { Dropdown } from 'antd';
import { v4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { slideAction, saveMirrorToCloudinary } from '../../store/slideSlice';
import { slideAPI } from '../../store/api';
import { getSnapshot } from '../../utils/utils';
import { emitElementAdd } from '../../Socket/Socket';
import { getUpdatedSlides } from '../../utils/helper';
import LayoutEnum from '../../utils/layoutEnum';
import { SMART_ART_PRESETS, createSmartArtObject } from './smartArtPresets';
import { computeSmartArtLayout } from './layouts';
import { normalizeNodes } from './smartArtUtils';
import classes from './SmartArtDropdown.module.css';

const PAGE_WIDTH =
  window.screen.width > 2200 ? 1800 :
  window.screen.width > 1540 ? 1200 :
  window.screen.width > 1200 ? 960 :
  window.screen.width > 992 ? 848 :
  window.screen.width > 768 ? 700 :
  window.screen.width > 490 ? 490 : 300;

const PAGE_HEIGHT =
  window.screen.width > 2200 ? 1020 :
  window.screen.width > 1540 ? 700 :
  window.screen.width > 1200 ? 540 :
  window.screen.width > 992 ? 500 :
  window.screen.width > 768 ? 400 :
  window.screen.width > 490 ? 290 : 180;

export const PresetPreview = ({ preset, className }) => {
  const { boxes, connectors } = computeSmartArtLayout(
    preset.layout,
    normalizeNodes(preset.nodes),
    preset.theme,
    preset.variant
  );
  return (
    <svg className={className || classes.menuPreview} viewBox="0 0 100 100" preserveAspectRatio="none">
      {connectors.map((c, i) => (
        <line key={i} x1={c.x1Pct} y1={c.y1Pct} x2={c.x2Pct} y2={c.y2Pct} stroke="#9aa0a6" strokeWidth="1" />
      ))}
      {boxes.map((b) => {
        if (b.shapeType === 'oval') {
          return <ellipse key={b.nodeId} cx={b.xPct + b.wPct / 2} cy={b.yPct + b.hPct / 2} rx={b.wPct / 2} ry={b.hPct / 2} fill={b.fill} />;
        }
        if (b.shapeType === 'trapezoid') {
          const x = b.xPct; const y = b.yPct; const w = b.wPct; const h = b.hPct;
          return <polygon key={b.nodeId} points={`${x + w * 0.18},${y} ${x + w * 0.82},${y} ${x + w},${y + h} ${x},${y + h}`} fill={b.fill} />;
        }
        return <rect key={b.nodeId} x={b.xPct} y={b.yPct} width={b.wPct} height={b.hPct} rx="2" fill={b.fill} />;
      })}
    </svg>
  );
};

export const SmartArtDropdown = ({ currentSlideId, presentationId, onMenuOpenChange }) => {
  const dispatch = useDispatch();
  const [menuOpen, setMenuOpen] = useState(false);

  // Reported so a wrapping toolbar tooltip can stay down while the menu is up.
  useEffect(() => {
    if (onMenuOpenChange) onMenuOpenChange(menuOpen);
  }, [menuOpen, onMenuOpenChange]);
  const isMasterSlide = useSelector((state) => state.slide.isMasterSlide);
  const masterSlideId = useSelector((state) => state.slide.masterSlideId);
  const existingSlides = useSelector((state) => state.slide.slides);
  const mirrorSlides = useSelector((state) => state.slide.mirrorSlides);
  const currentSlideData = useSelector(
    (state) => currentSlideId && state.slide.slides[currentSlideId]
  );

  const insertSmartArt = (presetId) => {
    if (!currentSlideId) return;
    const preset = SMART_ART_PRESETS[presetId];
    if (!preset) return;

    let slideContent = document.getElementById('slide01');
    if (isMasterSlide) slideContent = document.getElementById('master-slide');
    if (!slideContent) return;

    let activeSlideId = currentSlideId;
    const lockScreen = document.createElement('div');
    lockScreen.setAttribute(
      'style',
      'width:100%;height:100%;position:absolute;background:transparent;cursor:crosshair;z-index:9999999999'
    );
    slideContent.appendChild(lockScreen);

    let draw = false;
    let tempBox;
    let x1; let y1; let x2; let y2;

    const mouseDown = (e) => {
      e.preventDefault();
      draw = true;
      x1 = e.offsetX;
      y1 = e.offsetY;
      tempBox = document.createElement('div');
      tempBox.style.position = 'absolute';
      tempBox.style.border = '2px dashed #1a73e8';
      tempBox.style.background = 'rgba(26,115,232,0.06)';
      tempBox.style.boxSizing = 'border-box';
      tempBox.style.left = `${x1}px`;
      tempBox.style.top = `${y1}px`;
      lockScreen.appendChild(tempBox);
    };

    const mouseMove = (e) => {
      if (!draw || !tempBox) return;
      x2 = e.offsetX;
      y2 = e.offsetY;
      tempBox.style.left = `${Math.min(x1, x2)}px`;
      tempBox.style.top = `${Math.min(y1, y2)}px`;
      tempBox.style.width = `${Math.abs(x2 - x1)}px`;
      tempBox.style.height = `${Math.abs(y2 - y1)}px`;
    };

    const mouseUp = () => {
      if (tempBox) lockScreen.removeChild(tempBox);
      if (isMasterSlide && masterSlideId) activeSlideId = masterSlideId;

      // default to a sensible size if the user just clicked
      if (x2 === undefined || Math.abs(x2 - x1) < 20) x2 = x1 + PAGE_WIDTH * 0.5;
      if (y2 === undefined || Math.abs(y2 - y1) < 20) y2 = y1 + PAGE_HEIGHT * 0.45;

      const left = Math.min(x1, x2);
      const top = Math.min(y1, y2);
      const width = Math.abs(x2 - x1);
      const height = Math.abs(y2 - y1);

      const smartArtId = `smartart-${v4()}`;
      const data = createSmartArtObject(presetId);
      const style = {
        width: `${(width / PAGE_WIDTH) * 100}%`,
        height: `${(height / PAGE_HEIGHT) * 100}%`,
        position: 'absolute',
        left: `${(left / PAGE_WIDTH) * 100}%`,
        top: `${(top / PAGE_HEIGHT) * 100}%`,
        zIndex: 100,
      };

      const elementData = { smartArtId, currentSlideId: activeSlideId, style, data };
      const dbPayload = { ...data, styles: style };

      if (!isMasterSlide) {
        dispatch(slideAction.addSmartArtObject(elementData));
        const updatedSlides = getUpdatedSlides(elementData, 'smartart', existingSlides);
        // The mirror snapshot save fetch-mutate-saves the same document
        // server-side as the element create, so it must wait for the create
        // to land - otherwise it can race ahead and overwrite the document
        // with a stale copy that doesn't have the new SmartArt object yet.
        (async () => {
          await slideAPI.createNewElementInDatabase(
            presentationId,
            activeSlideId,
            smartArtId,
            dbPayload,
            'smartart',
            dispatch,
            { slides: updatedSlides, mirrorSlides }
          );
          setTimeout(() => {
            getSnapshot().then((dataUrl) => {
              dispatch(saveMirrorToCloudinary({ dataId: activeSlideId, mirrorSlideDataUrl: dataUrl, presentationId }));
            });
          }, 0);
        })();
      } else {
        const isMainMasterSlide = !Object.values(LayoutEnum).some((layout) =>
          currentSlideId.toLowerCase().includes(layout.toLowerCase())
        );
        if (isMainMasterSlide) {
          dispatch(slideAction.addSmartArtObject(elementData));
          Object.keys(existingSlides).forEach((slideId) => {
            if (Object.values(LayoutEnum).some((v) => slideId.includes(v))) {
              dispatch(
                slideAction.addSmartArtObject({
                  ...elementData,
                  style: { ...style, pointerEvents: 'none' },
                  currentSlideId: slideId,
                })
              );
            }
          });
          slideAPI.createMultipleNewElementInDatabase(presentationId, smartArtId, dbPayload, 'smartart');
        } else {
          const elementId = `${currentSlideId.split('-')[2]}-${smartArtId}`;
          dispatch(slideAction.addSmartArtObject({ ...elementData, smartArtId: elementId }));
          slideAPI.createMultipleNewElementInDatabase(
            presentationId,
            elementId,
            dbPayload,
            'smartart',
            currentSlideData?.layoutType
          );
        }
      }

      dispatch(slideAction.setActiveSmartArt(smartArtId));
      emitElementAdd({ roomId: presentationId, elementType: 'smartart', elementData });

      slideContent.style.cursor = 'default';
      slideContent.removeChild(lockScreen);
    };

    lockScreen.addEventListener('mousedown', mouseDown, { once: true });
    lockScreen.addEventListener('mousemove', mouseMove, false);
    lockScreen.addEventListener('mouseup', mouseUp, { once: true });
  };

  const menu = (
    <div id="smartart-menu" className={classes.galleryCard}>
      <div className={classes.galleryHeader}>Choose a layout</div>
      {Object.entries(SMART_ART_PRESETS).map(([key, preset]) => (
        <div
          key={key}
          className={classes.tile}
          onClick={() => {
            setMenuOpen(false);
            insertSmartArt(key);
          }}
        >
          <PresetPreview preset={preset} className={classes.tilePreview} />
          <span className={classes.tileLabel}>{preset.label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Dropdown
      overlay={menu}
      trigger={['click']}
      visible={menuOpen}
      onVisibleChange={setMenuOpen}
      disabled={!currentSlideId}
    >
      <span style={{ color: '#626262' }} onClick={(e) => e.preventDefault()}>
        <AccountTreeIcon
          fontSize="medium"
          style={{ width: '25px', height: '22px', verticalAlign: 'top' }}
          alt="SmartArt"
        />
      </span>
    </Dropdown>
  );
};

export default SmartArtDropdown;
